import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormGroup,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Rating,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,  
  Typography
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { UserContext } from "../UserContext";

import { getJobOrder } from "../API/jobOrderAPI";
import { getClient } from "../API/clientAPI";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CircleIcon from "@mui/icons-material/Circle";
import Stack from "@mui/material/Stack";

import ViewTermiteLocationCard from "./FormComponents/ViewTermiteLocationCard";
import ViewExterraCard from "./FormComponents/ViewExterraCard";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

import { JOB_TYPES, JOB_DESCRIPTIONS } from "../JobOrders/utils/JobOrderConstants";

function ViewTermiteControlForm() {
  const { id } = useParams();
  const [jobOrder, setJobOrder] = useState({});
  const [client, setClient] = useState({});
  const [error] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fromPayrollReports = searchParams.get('from') === 'payroll-reports';
  const returnTo = searchParams.get('returnTo');

  // Get user roles from localStorage (not UserContext)
  const storedUser = localStorage.getItem("user");
  const parsedUser = storedUser ? JSON.parse(storedUser) : {};
  const userRoles = parsedUser.roles || [];
  const isTechnician = userRoles.includes('operations_technician');

  useEffect(() => {
    const fetchJobOrder = async () => {
      try {
        setIsLoading(true);
        const data = await getJobOrder(id);
        const client = await getClient(data.client_id); // Fetch client using client_id from job order
        setClient(client || {});
        setJobOrder(data || {});
      } catch (err) {
        setErrorMessage("Error fetching job order:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchJobOrder(); // Fetch the job order if the ID exists
  }, [id, error]); // Dependency array ensures the effect runs when ID changes

  // const job_types = [
  //   "GPC",
  //   "Fogging",
  //   "Monitoring",
  //   "Deep Cleaning",
  //   "QTM",
  //   "Heat Treatment",
  //   "Vacuuming",
  //   "Termite Control",
  //   "Comprehensive Termite",
  //   "Termite Baiting",
  //   "Termite Spot Spraying",
  //   "AG Installation",
  //   "Phase 1 and Phase 2",
  //   "QPMS",
  //   "Ant Control",
  //   "Cockroach Control",
  //   "Mosquito Control",
  //   "Cockroach Gel Bait/Powder",
  //   "Basic Rodent Control",
  //   "Intensive Rodent Control",
  //   "Termite Inspection",
  //   "Disinfection",
  //   "Billing",
  //   "Collection",
  //   "Deliver Service Invoice",
  //   "Deliver Certificate",
  //   "QuickCert",
  //   "Ocular",
  //   "Rodent Monitoring",
  //   "Daily Monitoring",
  //   "Logistics",
  //   "ILT Monitoring",
  //   "Facility Audit",
  //   "Meeting/Seminar",
  //   "QA Visit",
  //   "Termipore Installation",
  //   "In2Care Installation",
  //   "In2Care Monitoring",
  //   "Exterra Inground",
  //   "Delivery of Materials",
  //   "Backjob"
  // ];

  // const job_description = [
  //   "Spraying",
  //   "Misting",
  //   "Fogging",
  //   "Mistblower",
  //   "Rodent Trapping",
  //   "Rodent Monitoring",
  //   "Logistics (Billing, Collection, Delivery)",
  //   "AG Installation",
  //   "Comprehensive Termite Control",
  //   "Termatrac Monitoring",
  //   "Wiping",
  //   "Disinfection",
  //   "UV Light",
  //   "Cold Fogging",
  //   "AG Monitoring",
  //   "Powder/Gel Baiting",
  //   "Soil Poisoning",
  //   "AG Pullout",
  //   "QA Visit/Facility Audit",
  //   "GPC with Blower and Vacuum",
  //   "Termipore, In2Care, or Device Installation",
  //   "In2Care Monitoring"
  // ];

  // helper to normalize labels into keys
  const formatKey = (s) => s.toLowerCase().replace(/\s+/g, "_");

  // map raw keys back to label arrays and sort alphabetically
  const displayedJobTypes = JOB_TYPES
    .filter((label) => (jobOrder.job_types || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));

  const displayedJobDescriptions = JOB_DESCRIPTIONS
    .filter((label) => (jobOrder.job_description || []).includes(formatKey(label)))
    .sort((a, b) => a.localeCompare(b));
  
  const STRUCTURE_TYPE_OPTIONS = [
    { key: "residential", label: "Residential" },
    { key: "commercial",  label: "Commercial"  },
    { key: "industrial",  label: "Industrial"  },
  ];

  // Normalize truthy values (true / "true" / 1 / "yes", etc.)
  const isTrue = (v) =>
    typeof v === "boolean" ? v
    : typeof v === "number" ? v === 1
    : typeof v === "string" ? ["true","1","yes","y","on"].includes(v.trim().toLowerCase())
    : false;

  // Compute only the selected types, in display order
  const displayedStructureTypes = STRUCTURE_TYPE_OPTIONS
    .filter(opt => isTrue(jobOrder?.structure_type?.[opt.key]))
    .map(opt => opt.label);

  // helper keeps zeros, and prints — for empty/undefined
  const displayValue = (v) => {
    if (v === 0 || v === "0") return "0";
    if (v == null) return "—";
    const s = String(v).trim();
    return s.length ? s : "—";
  };

  const sd = jobOrder?.structure_details ?? {};

  const normalize = (v) => String(v ?? "").trim().toLowerCase();

  const ta = normalize(jobOrder?.termite_activity);

  // helper for termite recommendations
  const cleanList = (arr) =>
  (arr ?? [])
    .map((s) => String(s ?? "").trim())
    .filter(Boolean); // remove empty strings/nulls

  // inside your component (above return)
  const termiteRecommendations = cleanList(jobOrder?.termite_recommendations);


  return (
    <BaseFormWrapper
      titleIcon={<ReceiptLongIcon />}
      title="Operations"
      subTitle="Service Reports"
      subTitleRoute="/manage-service-reports"
      formTitle="View Service Report"
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Basic Information
                </Typography>
              }
            />
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {" "}
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} lg={2}>
                  <TextField
                    name="id"
                    label="Job Order ID"
                    value={jobOrder?.id || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={2}>
                  <FormControl fullWidth>
                    <InputLabel id="job-order-status-label">
                      Job Order Status
                    </InputLabel>
                    <Select
                      labelId="job-order-status-label"
                      name="status"
                      value={jobOrder?.status || ""}
                      label="Job Order Status"
                      disabled
                    >
                      <MenuItem value="Not Done">Not Done</MenuItem>
                      <MenuItem value="Rescheduled">Rescheduled</MenuItem>
                      <MenuItem value="Done">Done</MenuItem>
                      <MenuItem value="Cancelled By Client">
                        Cancelled By Client
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client ID"
                    label="Client ID"
                    value={jobOrder?.client_id || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="Client Name"
                    label="Client Name"
                    value={jobOrder?.client?.name || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={10}>
                      <TextField
                        label="Job Site Address"
                        variant="outlined"
                        fullWidth
                        value={
                          jobOrder?.client?.address
                            ? `${jobOrder.client.address.house_unit_street || ""
                            }, ${jobOrder.client.address.barangay || ""
                            }, ${jobOrder.client.address.city || ""
                            }, ${jobOrder?.client?.address.metro_province || ""} ${jobOrder.client.address.zipcode || ""}`
                            : ""
                        }
                        disabled
                      />
                    </Grid>

                    <Grid item xs={12} md={12} lg={2}>
                      <TextField
                        name="area"
                        label="Area"
                        value={jobOrder?.client?.address?.area || ""}
                        fullWidth
                        disabled
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Person"
                    variant="outlined"
                    value={
                      jobOrder.client?.contact
                        ? `${jobOrder.client.contact.lastname || ""}, ${jobOrder.client.contact.firstname || ""
                        }`
                        : ""
                    } // Display the contact's full name or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    variant="outlined"
                    value={jobOrder.client?.contact?.email?.email || ""} // Display the contact email or an empty string if not available
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    fullWidth
                    label="Contact Number"
                    variant="outlined"
                    value={
                      jobOrder.client?.contact?.number
                        ? `${jobOrder.client.contact.number.type}: ${jobOrder.client.contact.number.number}`
                        : ""
                    } // Display the contact number or an empty string if not available
                    disabled
                  />
                </Grid>                
                <Grid item xs={12} md={12} lg={4}>
                  <TextField
                    label="Location Type"
                    value={
                      client?.location_type
                        ? client.location_type
                        : ""
                    }
                    fullWidth
                    variant="outlined"
                    sx={{ fontSize: "0.875rem" }}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Type Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Type</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobTypes.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Job Description Section */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader title={<Typography variant="subtitle2">Job Description</Typography>} sx={{ pb: 0 }} />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                {displayedJobDescriptions.map((option, idx) => (
                  <Box key={idx} component="li" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: '0.5rem' }} />
                    <Typography variant="body2">{option}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Special Notes Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{                    
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Special Notes
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Special Notes"
                    name="special_notes"
                    type="text"
                    value={jobOrder.special_notes || ""}
                    fullWidth
                    disabled
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appointment Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Appointment
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={6}>
                  <FormControl fullWidth>
                    <InputLabel id="appointment-type-label">
                      Appointment Type
                    </InputLabel>
                    <Select
                      labelId="appointment-type-label"
                      name="appointment_type"
                      value={jobOrder?.appointment_type || ""}
                      label="appointment_type"
                      disabled
                    >
                      <MenuItem value="Initial">Initial</MenuItem>
                      <MenuItem value="Follow-up">Follow-up</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                  <FormControl fullWidth>
                    <InputLabel id="treatment-type-label">
                      Treatment Type
                    </InputLabel>
                    <Select
                      labelId="treatment-type-label"
                      name="treatment_type"
                      value={jobOrder?.treatment_type || ""}
                      label="treatment_type"
                      disabled
                    >
                      <MenuItem value="QB">QB</MenuItem>
                      <MenuItem value="FFUP">FFUP</MenuItem>
                      <MenuItem value="LT">LT</MenuItem>
                      <MenuItem value="QBLT">QBLT</MenuItem>
                      <MenuItem value="Backjob">Backjob</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Structure Type Section (view-only) */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardHeader
              title={<Typography variant="subtitle2">Structure Type</Typography>}
              sx={{ pb: 0 }}
            />
            <CardContent sx={{ pt: 1, px: 2 }}>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                {displayedStructureTypes.length > 0 ? (
                  displayedStructureTypes.map((label, idx) => (
                    <Box
                      key={idx}
                      component="li"
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <CircleIcon fontSize="inherit" sx={{ mr: 1, fontSize: "0.5rem" }} />
                      <Typography variant="body2">{label}</Typography>
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    None selected
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Structure Details (view-only) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Structure Details
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Number of Bedrooms
                  </Typography>
                  <Typography variant="body1">{displayValue(sd.bedrooms)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Number of Restrooms
                  </Typography>
                  <Typography variant="body1">{displayValue(sd.restrooms)}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    Number of Floors
                  </Typography>
                  <Typography variant="body1">{displayValue(sd.floors)}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Termite Activity (view-only) */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Termite Activity
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <FormGroup sx={{ width: "100%" }}>
                <FormControlLabel
                  sx={{
                    width: "100%",
                    opacity: ta === "with" ? 1 : 0.75,
                  }}
                  control={
                    <Checkbox
                      checked={ta === "with"}
                      disabled
                    />
                  }
                  label="With Active Termites"
                />

                <FormControlLabel
                  sx={{
                    width: "100%",
                    opacity: ta === "without" ? 1 : 0.75,
                  }}
                  control={
                    <Checkbox
                      checked={ta === "without"}
                      disabled
                    />
                  }
                  label="Without Active Termites"
                />
              </FormGroup>

              {ta !== "with" && ta !== "without" && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  No selection
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Inspection & Pest Count (Mobile) — view-only, wrapped in a parent card */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Inspection Report
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {(() => {
                const list = (jobOrder?.inspection_report || []).filter((r) => {
                  const keys = [
                    "location",
                    "location_other",
                    "findings",
                    "action_taken",
                    "bait_monitor_location",
                    "remarks",
                  ];
                  return keys.some((k) => (r?.[k] ?? "").toString().trim() !== "");
                });

                return list.length ? (
                  <Stack spacing={2}>
                    {list.map((item, idx) => (
                      <ViewTermiteLocationCard key={idx} item={item} />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No inspection items
                  </Typography>
                );
              })()}
            </CardContent>
          </Card>
        </Grid>

        {/* Recommendations Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2, mt: 2 }}>
            <CardHeader
              title={
                <Typography variant="h6" sx={{ fontWeight: "bold", textTransform: "uppercase" }}>
                  Recommendations
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              {termiteRecommendations.length ? (
                <Box component="ul" sx={{ m: 0, pl: 3, listStyle: "disc" }}>
                  {termiteRecommendations.map((label, i) => (
                    <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>
                      {label}
                    </Typography>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">None selected</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Exterra Section */}
        <Grid item xs={12}>
          <ViewExterraCard stations={jobOrder?.exterra_bait_stations} />
        </Grid>

        {/* Chemical Used Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
              marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
              flex: 1,
            }}
          >
            <CardContent sx={{ flex: 1 }}>
              <Grid item xs={12}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Chemical Used / Batch # / Expiration
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: "bold" }}
                        >
                          Dilution
                        </TableCell>
                        <TableCell
                          align="center"
                          colSpan={2}
                          sx={{ fontWeight: "bold" }}
                        >
                          Equipment Used
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(jobOrder.chemical_used || []).map((chem, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{chem.label}:</TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.chemical || ""}
                              disabled
                              sx={{ minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              fullWidth
                              variant="outlined"
                              size="small"
                              value={chem.dilution || ""}
                              disabled
                              sx={{ minWidth: 50 }}
                            />
                          </TableCell>
                          {jobOrder.equipment_used?.length > idx ? (
                            <>
                              <TableCell>
                                {jobOrder.equipment_used[idx].label}
                              </TableCell>
                              <TableCell>
                                <Checkbox
                                  checked={jobOrder.equipment_used[idx].usage}
                                  disabled
                                />
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell />
                              <TableCell />
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Reminder Notes Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Reminder Notes
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Reminder Notes"
                value={jobOrder.reminder_notes || ""}
                fullWidth
                multiline
                rows={6}
                size="small"
                disabled
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Service Fee Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
                >
                  Service Fee
                </Typography>
              }
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TextField
                label="Service Fee"
                value={jobOrder.service_fee || ""}
                fullWidth
                size="small"
                disabled
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₱</InputAdornment>
                  )
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Payment Status Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={<Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>Payment Status</Typography>}
              sx={{ pb: 0, px: 2, pt: 2 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none' }}>
                <Table size="small">
                  <TableBody>
                    {(jobOrder.service_report_payment_status || []).map((statusOpt, idx) => {
                      const methodOpt = (jobOrder.service_report_payment_method || [])[idx] || {};
                      return (
                        <TableRow key={statusOpt.label}>
                          <TableCell>{statusOpt.label.replace(/_/g, ' ')}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={statusOpt.selection}
                              disabled
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ color: 'text.secondary', p: 0 }}>
                            •
                          </TableCell>
                          <TableCell />
                          <TableCell>{methodOpt.label?.replace(/_/g, ' ') || ""}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={methodOpt.selection}
                              disabled
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Service Accomplished by Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Service Accomplished by:
              </Typography>
              <Grid container spacing={2}>
                { (jobOrder.accomplished_by_technician || jobOrder.technicians || [])
                    .map((name, i) =>
                      typeof name === "string"
                        ? name
                        : `${name.firstname} ${name.lastname}`
                    )
                    .map((techName, i) => (
                  <Grid key={i} item xs={12}>
                    <TextField
                      label={`Technician ${i + 1}`}
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={techName}
                      disabled
                    />
                  </Grid>
                ))}

                {/* Accomplished Signature */}
                <Grid item xs={12}>
                  {jobOrder.accomplishedSignature && (
                    <Box
                      component="img"
                      src={jobOrder.accomplishedSignature}
                      alt="Accomplished Signature"
                      sx={{
                        width: "100%",
                        maxWidth: 400,
                        height: 120,
                        objectFit: "contain",
                        border: "1px solid rgba(0,0,0,0.3)",
                        mb: 2,
                      }}
                    />
                  )}
                </Grid>

                {/* Technician Signatory dropdown, right below the signature */}
                <Grid item xs={12}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="technician-signatory-label">
                      Technician Signatory
                    </InputLabel>
                    <Select
                      labelId="technician-signatory-label"
                      value={jobOrder.technician_signatory || ""}
                      label="Technician Signatory"
                      disabled
                    >
                      {jobOrder.technicians?.map((tech) => (
                        <MenuItem
                          key={tech.id}
                          value={`${tech.firstname} ${tech.lastname}`}
                        >
                          {`${tech.firstname} ${tech.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* ⏰ TIMESTAMP DISPLAY - Technician Signature (HIDDEN FROM TECHNICIANS) */}
                {!isTechnician && jobOrder.technician_signature_uploaded_at && (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                      <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 600 }}>
                        🔵 Technician Signature Uploaded At:
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#0d47a1', mt: 0.5 }}>
                        {new Date(jobOrder.technician_signature_uploaded_at).toLocaleString('en-US', {
                          month: 'short',
                          day: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: true
                        })}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Schedule Section */}
        <Grid
          item
          xs={12}
          md={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Card
            variant="outlined"
            sx={{
                marginBottom: { xs: "5px", md: "5px", lg: "10px", xl: "20px" },
                flex: 1,
              }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Schedule
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Date"
                    name="job_date"
                    type="date"
                    value={jobOrder?.job_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Start Time"
                    name="start_time"
                    type="time"
                    value={jobOrder?.start_time || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
                {/* <Grid item xs={12} md={6}>
                  <TextField
                    label="End Date"
                    name="end_date"
                    type="date"
                    value={jobOrder?.end_date || ""}
                    fullWidth
                    disabled
                  />
                </Grid> */}
                <Grid item xs={12} md={4}>
                  <TextField
                    label="End Time"
                    name="end_time"
                    type="time"
                    value={jobOrder?.end_time || ""}
                    fullWidth
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      
        {/* Acknowledged Signature Section */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardHeader
              title={
                <Typography variant="subtitle2"
                  sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}>
                  For Client Only
                </Typography>
              }
              sx={{ px: 2, pt: 2, pb: 0 }}
            />
            <CardContent sx={{ px: 2, pb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Your Satisfaction Rating:
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                (1 – lowest, 5 – highest)
              </Typography>
              <Rating
                name="read-only"
                value={Number(jobOrder.satisfaction_rating) || 0}
                readOnly
              />

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                Acknowledged By:
              </Typography>
              {jobOrder.acknowledgedSignature && (
                <Box
                  component="img"
                  src={jobOrder.acknowledgedSignature}
                  alt="Acknowledged Signature"
                  sx={{
                    width: "100%",
                    maxWidth: 400,
                    height: 120,
                    objectFit: "contain",
                    border: "1px solid rgba(0,0,0,0.3)",
                    mb: 2,
                  }}
                />
              )}
              <TextField
                label="Client Representative"
                variant="outlined"
                fullWidth
                size="small"
                value={jobOrder.acknowledgedClientSignatory || ""}
                disabled
              />

              {/* ⏰ TIMESTAMP DISPLAY - Client Signature (HIDDEN FROM TECHNICIANS) */}
              {!isTechnician && jobOrder.client_signature_uploaded_at && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                    🟢 Client Signature Uploaded At:
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#1b5e20', mt: 0.5 }}>
                    {new Date(jobOrder.client_signature_uploaded_at).toLocaleString('en-US', {
                      month: 'short',
                      day: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Document Links Section */}
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{ padding: "20px", marginBottom: "20px", flex: 1 }}
          >
            <CardHeader
              title={
                <Typography
                  variant="h6"
                  sx={{
                    paddingBottom: "1rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "1rem",
                  }}
                >
                  Document Links
                </Typography>
              }
            />
            <CardContent sx={{ flex: 1 }}>
              <Grid container spacing={3}>
                {jobOrder?.receipts_url ? (
                  <>
                    {jobOrder?.receipts_url && (
                      <Grid item xs={12}>
                        <Box>
                          <Link
                            href={jobOrder.receipts_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "none",
                              color: "primary.main",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            Receipts.pdf{" "}
                            <OpenInNewIcon
                              sx={{
                                marginLeft: 1,
                                fontSize: "1rem",
                              }}
                            />
                          </Link>
                        </Box>
                      </Grid>
                    )}
                  </>
                ) : (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="textSecondary">
                      No document links available.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* ⏰ TIMESTAMP DISPLAY - Report Submission (HIDDEN FROM TECHNICIANS) */}
        {!isTechnician && jobOrder.report_submitted_at && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 600 }}>
                📋 Report Submitted At:
              </Typography>
              <Typography variant="body2" sx={{ color: '#bf360c', mt: 0.5 }}>
                {new Date(jobOrder.report_submitted_at).toLocaleString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: true
                })}
              </Typography>
            </Box>
          </Grid>
        )}

        {/* Cancel Button Section */}
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          mt={2}
          mb={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (returnTo) {
                // Use window.location.href to force a full page navigation
                window.location.href = returnTo;
              } else if (fromPayrollReports) {
                navigate("/payroll-reports");
              } else {
                navigate("/manage-service-reports");
              }
            }}
            sx={{
              fontSize: "0.875rem",
              color: "#FF4C4C",
              borderColor: "#FF4C4C",
            }}
            disabled={isLoading}
          >
            {fromPayrollReports ? "Back to Payroll Reports" : "Cancel"}
          </Button>
        </Grid>
      </Grid>
    </BaseFormWrapper>
  );
}

export default ViewTermiteControlForm;
